import { compact } from 'lodash';
import { usePostHog } from 'posthog-js/react';
import { PropsWithChildren, useEffect, useState } from 'react';
import { useLocation, useMatches } from 'react-router-dom';
import { useTitle } from 'react-use';

import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';

type UnextendedMatch = ReturnType<typeof useMatches>[0];

export interface Match extends UnextendedMatch {
  handle: {
    title?: string;
  };
}

const DEFAULT_TITLE = 'Luminary';

export function RouteWatcher({ children }: PropsWithChildren<unknown>) {
  const location = useLocation();
  const posthog = usePostHog();
  const [title, setTitle] = useState(DEFAULT_TITLE);

  const { displayName: clientName } = useHouseholdDetailsContext();

  const matches = useMatches() as Match[];

  useEffect(() => {
    let title: string | null = null;

    // Match to the most specific specified title
    matches.forEach((match) => {
      if (match.handle?.title) {
        title = match.handle.title;
      }
    });

    setTitle(compact([DEFAULT_TITLE, clientName, title]).join(' | '));
  }, [clientName, matches]);

  // Track pageviews
  useEffect(() => {
    if (posthog) {
      posthog.capture('$pageview', {
        $current_url: window.location.href,
      });
    }
  }, [location, posthog]);

  // Publish the route that has actually rendered.
  //
  // react-router v7 wraps navigation in React.startTransition, so the URL can
  // change a frame or more before the new route commits. That makes
  // `page.waitForURL(...)` an unreliable "the page is ready" signal in tests --
  // it resolves while the previous route is still on screen. This effect runs
  // after commit, so the attribute only ever names a route that is rendered.
  // Read by `waitUntilRouteLoaded` in the Playwright suite.
  useEffect(() => {
    document.documentElement.dataset.renderedPath = location.pathname;
  }, [location.pathname]);

  useTitle(title);

  return <>{children}</>;
}
